import { Injectable } from '@angular/core';
import { MergeStrategy } from '@ngrx/data';
import {
  CachedStream,
  ClearCache,
  GlobalConfigManager,
  KD_DEFAULT_CONFIG,
  PageParams,
  getIdContextKey,
} from '@selfai-platform/shared';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  catchError,
  combineLatest,
  filter,
  map,
  of,
  startWith,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { GlobalActiveFiltersService } from '../../filters';
import { RecycleBinApiToDomainService } from '../../recycle-bin';
import { Filter } from '../../workbook';
import { getAllFiltersDsRelations } from '../functions';
import { BoardWidgetOptions, Dashboard, DashboardList, LayoutWidgetInfo, WidgetDashboardState } from '../models';
import { DashboardStore, WidgetDashboardStore } from '../stores';
import { DashboardApiToDomainService } from './dashboard-api-to-domain.service';

@Injectable({ providedIn: 'root' })
export class DashboardDomainService {
  private readonly activeWidgetRequest$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly dashboardApiToDomainService: DashboardApiToDomainService,
    private readonly recycleBinApiToDomainService: RecycleBinApiToDomainService,
    private readonly dashboardStore: DashboardStore,
    private readonly widgetDashboardStore: WidgetDashboardStore,
    private readonly globalActiveFiltersService: GlobalActiveFiltersService,
  ) {}

  @CachedStream({
    ttl: () =>
      GlobalConfigManager.tryGetInstance().getConfig()?.knowledgeDesigner?.cache?.dashboard ??
      KD_DEFAULT_CONFIG.cache.dashboard,
    keyExtractor: (...args: unknown[]) => {
      const [dashboardId, widgetContextId] = args;

      return widgetContextId ? `${dashboardId}_${widgetContextId}` : (dashboardId as string);
    },
  })
  loadDashboard(dashboardId: string, widgetContextId?: string): Observable<Dashboard> {
    return this.dashboardStore.getByKey(dashboardId, { mergeStrategy: MergeStrategy.OverwriteChanges }).pipe(
      filter((dashboard) => !!dashboard),
      take(1),
      tap((dashboard) => {
        this.dashboardStore.upsertOneInCache({ id: dashboardId, ...dashboard, contextId: widgetContextId });
        this.updateWidgetsInCache(dashboardId, dashboard, widgetContextId);
        this.globalActiveFiltersService.setFilters(dashboardId, dashboard.configuration.filters, widgetContextId);
      }),
    );
  }

  loadDashboardByWidgetId(widgetId: string, widgetContextId?: string): Observable<Dashboard> {
    return this.widgetDashboardStore.getByKey(widgetId, { mergeStrategy: MergeStrategy.OverwriteChanges }).pipe(
      tap((dashboard) => {
        this.dashboardStore.upsertOneInCache({ id: dashboard.id, ...dashboard, contextId: widgetContextId });
        this.widgetDashboardStore.upsertOneInCache({ ...dashboard, widgetId, contextId: widgetContextId });
        this.globalActiveFiltersService.setFilters(dashboard.id, dashboard.configuration.filters, widgetContextId);
      }),
    );
  }

  loadDashboardByWidgetIdIfNotLoaded(widgetId: string, widgetContextId?: string): Observable<Dashboard | null> {
    return this.getDashboardByWidgetId(widgetId, widgetContextId).pipe(
      switchMap((dashboard) => {
        if (dashboard) {
          return this.globalActiveFiltersService.getFields(dashboard.id, widgetContextId).pipe(
            map((filters) => {
              if (filters.length === 0 && dashboard.configuration.filters.length > 0) {
                this.globalActiveFiltersService.setFilters(
                  dashboard.id,
                  dashboard.configuration.filters,
                  widgetContextId,
                );
              }

              return dashboard;
            }),
          );
        }

        return of(null);
      }),
      take(1),
      tap((dashboard) => {
        if (!dashboard) {
          this.loadDashboardByWidgetId(widgetId, widgetContextId).subscribe();
        }
      }),
    );
  }

  loadDashboardByWidgetIdQueued(widgetId: string, contextId?: string): Observable<Dashboard> {
    const cached$ = this.getDashboardByWidgetId(widgetId, contextId).pipe(startWith(null));

    return combineLatest([cached$, this.activeWidgetRequest$]).pipe(
      filter(([cached, isActive]) => !!cached || !isActive),
      switchMap(([cached]) => {
        if (cached) return of(cached);

        if (this.tryBecomeActive()) {
          return this.makeActiveRequest(widgetId, contextId);
        }

        return EMPTY;
      }),
      filter(Boolean),
      take(1),
    );
  }

  linkDashboardToWidget({
    dashboardId,
    widgetId,
    widgetContextId,
  }: {
    dashboardId: string;
    widgetId: string;
    widgetContextId?: string;
  }): void {
    this.getDashboard(dashboardId)
      .pipe(filter(Boolean))
      .subscribe((dashboard) => {
        this.widgetDashboardStore.upsertOneInCache({ ...dashboard, widgetId, contextId: widgetContextId });
      });
  }

  getDashboard(dashboardId: string): Observable<Dashboard> {
    return this.dashboardStore.entityMap$.pipe(
      map((entityMap) => entityMap?.[dashboardId]),
      filter(Boolean),
    );
  }

  getDashboardLoading(): Observable<boolean> {
    return this.dashboardStore.loading$;
  }

  getDashboardError(): Observable<unknown> {
    return this.dashboardStore.errors$;
  }

  getBoardWidgetOptions(dashboardId: string): Observable<BoardWidgetOptions> {
    return this.getDashboard(dashboardId).pipe(
      map((dashboard) => {
        return dashboard.configuration.options.widget;
      }),
    );
  }

  getBoardWidgetOptionsByWidgetId(widgetId: string): Observable<BoardWidgetOptions> {
    return this.getDashboardByWidgetId(widgetId).pipe(
      filter(Boolean),
      map((dashboard) => {
        return dashboard.configuration.options.widget;
      }),
    );
  }

  getDashboardByWidgetId(widgetId: string, widgetContextId?: string): Observable<Dashboard> {
    return this.widgetDashboardStore.entityMap$.pipe(
      map((entityMap) => entityMap?.[getIdContextKey(widgetId, widgetContextId)]),
    );
  }

  getDashboardByWidgetIdLoading(): Observable<boolean> {
    return this.widgetDashboardStore.loading$;
  }

  getLayoutWidgetInfoByWidgetId(widgetId: string): Observable<LayoutWidgetInfo> {
    return this.getDashboardByWidgetId(widgetId).pipe(
      map((dashboard) => dashboard.configuration.widgets.find((item) => item.ref === widgetId)),
    ) as Observable<LayoutWidgetInfo>;
  }

  getAllFiltersDsRelations(dashboardId: string, engineName: string, paramFilters?: Filter[]): Observable<Filter[]> {
    return this.getDashboard(dashboardId).pipe(
      map((dashboard) => {
        return getAllFiltersDsRelations(dashboard, engineName, paramFilters);
      }),
    );
  }

  addDashboard(workbookId: string, dashboard: Dashboard): Observable<Dashboard> {
    return this.dashboardStore.add({ ...dashboard, workbookId });
  }

  @ClearCache(['loadDashboard'], (...args: unknown[]) => (args[0] as Partial<Dashboard>).id)
  updateDashboard(dashboard: Partial<Dashboard> & Pick<Dashboard, 'configuration' | 'id'>): Observable<Dashboard> {
    return this.dashboardStore.update(dashboard);
  }

  loadDashboardList(workbookId: string, pageParams?: PageParams): Observable<DashboardList> {
    return this.dashboardApiToDomainService.loadDashboardList(workbookId, pageParams);
  }

  copyDashboard(dashboardId: string): Observable<void> {
    return this.dashboardApiToDomainService.copyDashboard(dashboardId);
  }

  deleteDashboard(dashboardId: string): Observable<void> {
    return this.dashboardApiToDomainService.deleteDashboard(dashboardId);
  }

  removeToTrash(dashboardId: string): Observable<unknown> {
    return this.recycleBinApiToDomainService.recycleBinDashboard(dashboardId);
  }

  private updateWidgetsInCache(dashboardId: string, dashboard: Dashboard, widgetContextId?: string): void {
    const updatedEntities: Partial<WidgetDashboardState>[] = [];

    dashboard.configuration.widgets.forEach((widget) => {
      updatedEntities.push({
        ...dashboard,
        widgetId: widget.ref,
        contextId: widgetContextId,
      });

      if (!widgetContextId) {
        updatedEntities.push({
          ...dashboard,
          widgetId: widget.ref,
          contextId: 'view',
        });
      }
    });

    this.widgetDashboardStore.upsertManyInCache(updatedEntities);
  }

  private makeActiveRequest(widgetId: string, widgetContextId?: string): Observable<Dashboard> {
    return this.loadDashboardByWidgetId(widgetId, widgetContextId).pipe(
      tap((dashboard) => {
        this.updateWidgetsInCache(dashboard.id, dashboard, widgetContextId);
        // wait for update widgets in cache
        setTimeout(() => {
          this.activeWidgetRequest$.next(false);
        }, 0);
      }),
      catchError((error) => {
        this.activeWidgetRequest$.next(false);

        return throwError(() => error);
      }),
    );
  }

  private tryBecomeActive(): boolean {
    const hasActiveRequest = this.activeWidgetRequest$.value;
    if (hasActiveRequest) return false;

    this.activeWidgetRequest$.next(true);

    return true;
  }
}
